import React, { useState, useEffect, useMemo } from 'react'
import { useQuery } from 'react-query'
import { Container, GridItem, Flex, Text, Box, Button } from '@chakra-ui/react'
import { useStoreActions } from 'easy-peasy'
import { usePermissions, useExperienceManager } from '@hooks'
import { useQueryParams } from '@hooks/use-query-params'
import { LayoutGrid } from '@blueprinthq/joy'
import { ClinicianOverride, Loading } from '@components'
import { endpoints } from '@api'
import { useHistory } from 'react-router-dom'
import flagsmith from 'flagsmith'
import { FlagsmithFeatures } from '@constants/flagsmith'

import {
  AINotetaker,
  ConsentModule,
  SampleSessionsModule,
  SessionContainer
} from './components'
import {
  SessionNotesRemainingBanner,
  UploadModal
} from '../sessions/components'

const SessionList = () => {
  const {
    documentationAutomationFreeTierSessionLimitReached,
    isAdmin,
    isDocumentationAutomationEnabled
  } = useExperienceManager()
  const [overrideClinicianId, setOverrideClinicianId] = useState(null)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [
    hasLessThanNumberRecordedSessions,
    setHasLessThanNumberRecordedSessions
  ] = useState(true)
  const { hasPermission } = usePermissions()
  const history = useHistory()
  const queryParams = useQueryParams()
  const signup = queryParams.get('signup') === 'true'

  const openDocAutomationSessionLimitReachedModal = useStoreActions(
    actions => actions.modals.docAutomationSessionLimitReached.openModal
  )
  const openDocAutomationPlanModal = useStoreActions(
    actions => actions.modals.chooseDocumentationAutomationPlan.openModal
  )

  const getClinicianDashboardQueryKey = useMemo(() => {
    const key = [endpoints.getClinicianDashboardSessions.getCacheId()]
    if (overrideClinicianId) key.push(overrideClinicianId)

    return key
  }, [overrideClinicianId])

  const {
    data: sessionsData,
    isLoading: isSessionsLoading
  } = useQuery(getClinicianDashboardQueryKey, () =>
    endpoints.getClinicianDashboardSessions.request({ overrideClinicianId })
  )

  const { data: user } = useQuery(
    endpoints.getUserAccount.getCacheId(),
    endpoints.getUserAccount.request
  )

  const isCurrentUserSelected =
    !overrideClinicianId || overrideClinicianId === user?.id

  const hasRecordedSessions =
    !isSessionsLoading && sessionsData.nodes.some(s => s.hasAudio)

  const alwaysShowSampleSessions = flagsmith.hasFeature(
    FlagsmithFeatures.ALWAYS_SHOW_SAMPLE_SESSIONS
  )

  // once num recorded sessions >= this number, hide the sample sessions module
  const numberRecordedSessions = 5

  useEffect(() => {
    if (isCurrentUserSelected) {
      setHasLessThanNumberRecordedSessions(
        !isSessionsLoading &&
          sessionsData.nodes.filter(s => s.hasAudio && !s.excludeFromDAUsage)
            .length < numberRecordedSessions
      )
    }
  }, [isSessionsLoading, sessionsData, isCurrentUserSelected])

  const hasSupervisorRole = user?.roles?.some(role =>
    ['supervisor_clinician', 'supervisor', 'clinicadmin'].includes(role.name)
  )

  const hasAdminRole = user?.roles?.some(role => role.name === 'orgadmin')

  const showDropdown =
    hasPermission('*:any:*:*') || hasSupervisorRole || hasAdminRole

  useEffect(() => {
    if (!isSessionsLoading && !hasRecordedSessions && isCurrentUserSelected) {
      if (isDocumentationAutomationEnabled) {
        history.replace('/sessions/get-started')
      } else if (signup) {
        history.replace('/start-session/new-client?signup=true')
      }
    }
  }, [
    isDocumentationAutomationEnabled,
    isSessionsLoading,
    hasRecordedSessions,
    isCurrentUserSelected,
    user,
    signup,
    history
  ])

  if (!user) return null

  return (
    <Container mt={'24px'}>
      <LayoutGrid>
        <GridItem
          colSpan={{
            base: 12,
            sm: 6,
            md: 6
          }}
          colStart={{
            base: 0,
            sm: 2,
            md: 4
          }}
        >
          <Flex
            direction="column"
            align="space-between"
            justify="center"
            mb={showDropdown ? 4 : 8}
          >
            {showDropdown && (
              <Box mb={4}>
                <ClinicianOverride
                  user={user}
                  isSuperAdmin={hasPermission('*:any:*:*')}
                  canViewOrgDropdown={hasAdminRole}
                  onChange={id => setOverrideClinicianId(id)}
                />
              </Box>
            )}
            <Flex align="center" justify="space-between">
              <Text as="h1" fontSize="lg" fontWeight="bold">
                Sessions
              </Text>
              {!isDocumentationAutomationEnabled && (
                <Button
                  data-click-event-name="Sessions Index -> Clicked Start Session"
                  onClick={() => history.push('/start-session/select-client')}
                  w="146px"
                >
                  Start Session
                </Button>
              )}
            </Flex>
          </Flex>
          <SessionNotesRemainingBanner />
          <GridItem
            display={{ base: 'block', md: 'none' }}
            colSpan={{
              base: 12,
              md: 3
            }}
            colStart={{
              base: 0,
              md: 10
            }}
            mb={8}
          >
            {isDocumentationAutomationEnabled && (
              <>
                <AINotetaker
                  setUploadModalOpen={setUploadModalOpen}
                  openDocAutomationSessionLimitReachedModal={
                    openDocAutomationSessionLimitReachedModal
                  }
                  openDocAutomationPlanModal={openDocAutomationPlanModal}
                  isAdmin={isAdmin}
                  documentationAutomationFreeTierSessionLimitReached={
                    documentationAutomationFreeTierSessionLimitReached
                  }
                />
                {(alwaysShowSampleSessions ||
                  hasLessThanNumberRecordedSessions) && (
                  <SampleSessionsModule />
                )}
                {isAdmin && <ConsentModule />}
              </>
            )}
          </GridItem>
          <Box mb="64px">
            {isSessionsLoading ? (
              <Loading />
            ) : (
              <SessionContainer
                data={sessionsData}
                isLoading={isSessionsLoading}
              />
            )}
          </Box>
        </GridItem>
        <GridItem
          display={{ base: 'none', md: 'block' }}
          colSpan={{
            base: 12,
            md: 3
          }}
          colStart={{
            base: 0,
            md: 10
          }}
        >
          {isDocumentationAutomationEnabled && (
            <>
              <AINotetaker
                setUploadModalOpen={setUploadModalOpen}
                openDocAutomationSessionLimitReachedModal={
                  openDocAutomationSessionLimitReachedModal
                }
                openDocAutomationPlanModal={openDocAutomationPlanModal}
                isAdmin={isAdmin}
                documentationAutomationFreeTierSessionLimitReached={
                  documentationAutomationFreeTierSessionLimitReached
                }
              />
              {(alwaysShowSampleSessions ||
                hasLessThanNumberRecordedSessions) && <SampleSessionsModule />}
              {isAdmin && <ConsentModule />}
            </>
          )}
        </GridItem>
      </LayoutGrid>
      <UploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
      />
    </Container>
  )
}

export default SessionList
